import { Button, networkIconFor } from '@alison/ui';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import type { NetworkRef } from './flightCheckView.js';
import { formatScore, scoreText, scoreTint, useScoreThresholds } from './scoreScale.js';

export const CARD_SX: SxProps<Theme> = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
};

export const TEXT_STRONG = '#31343A';

export const TEXT_SOFT = '#818897';

export const DIVIDER = '#E9ECEF';

export const MISSING_SCORE = '—';

// A network with no icon shows its name, unless the caller already prints it (`labelled`).
export function NetworkIcon({
  network,
  size,
  labelled = false,
}: {
  network: NetworkRef;
  size: number;
  labelled?: boolean;
}): ReactNode {
  const Icon = networkIconFor(network.slug, network.label);
  if (Icon !== undefined) {
    return <Icon aria-label={network.label} sx={{ width: size, height: size, flexShrink: 0 }} />;
  }
  if (labelled) return null;
  return (
    <Typography
      component="span"
      data-testid="network-label"
      sx={{ fontSize: 12, fontWeight: 500, color: TEXT_STRONG, whiteSpace: 'nowrap' }}
    >
      {network.label}
    </Typography>
  );
}

// A sub-score the service could not compute shows a dash, with the service's reason on hover.
export function MissingScore({ reason, size }: { reason: string; size: number }): ReactNode {
  return (
    <Tooltip title={reason}>
      <Typography
        component="span"
        sx={{ fontSize: size, fontWeight: 500, lineHeight: 'normal', color: TEXT_SOFT }}
      >
        {MISSING_SCORE}
      </Typography>
    </Tooltip>
  );
}

export function CenteredSpinner(): ReactNode {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: '96px' }}>
      <CircularProgress aria-label="Loading" />
    </Box>
  );
}

export function LoadError({ onRetry }: { onRetry: () => void }): ReactNode {
  return (
    <Stack
      role="alert"
      sx={{ ...CARD_SX, borderRadius: '16px', p: '32px', gap: '16px', alignItems: 'flex-start' }}
    >
      <Typography sx={{ fontSize: 14, color: TEXT_STRONG }}>
        Something went wrong loading this page.
      </Typography>
      <Button variant="outlined" size="small" onClick={onRetry}>
        Try again
      </Button>
    </Stack>
  );
}

export function EmptyState({ title, body }: { title: string; body: string }): ReactNode {
  return (
    <Stack
      data-testid="flight-check-empty"
      sx={{
        ...CARD_SX,
        borderRadius: '16px',
        p: '48px',
        gap: '8px',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 500, color: TEXT_STRONG }}>{title}</Typography>
      <Typography sx={{ fontSize: 14, color: TEXT_SOFT, maxWidth: 520 }}>{body}</Typography>
    </Stack>
  );
}

export function ScorePill({ score }: { score: number }): ReactNode {
  const theme = useTheme();
  const thresholds = useScoreThresholds();
  return (
    <Box
      component="span"
      sx={{
        px: '8px',
        py: '3px',
        borderRadius: '10px',
        bgcolor: scoreTint(theme, score, thresholds),
        color: scoreText(theme, score, thresholds),
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 'normal',
        flexShrink: 0,
      }}
    >
      {formatScore(score)}
    </Box>
  );
}

export function ScoreValue({
  score,
  size,
  weight = 500,
}: {
  score: number;
  size: number;
  weight?: number;
}): ReactNode {
  const theme = useTheme();
  const thresholds = useScoreThresholds();
  return (
    <Typography
      component="span"
      sx={{
        fontSize: size,
        fontWeight: weight,
        lineHeight: 'normal',
        color: scoreText(theme, score, thresholds),
      }}
    >
      {formatScore(score)}
    </Typography>
  );
}

export function FilterChip({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        px: '11px',
        py: '9px',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'border.default',
        borderRadius: '9px',
        flexShrink: 0,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.9px',
          textTransform: 'uppercase',
          color: 'text.muted',
          lineHeight: 'normal',
        }}
      >
        {label}
      </Typography>
      <Typography component="span" sx={{ fontSize: 12, color: TEXT_STRONG, lineHeight: 'normal' }}>
        {value}
      </Typography>
      <KeyboardArrowDown sx={{ fontSize: 16, color: 'text.muted' }} />
    </Box>
  );
}

export function OutlineButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}): ReactNode {
  return (
    <Button
      variant="outlined"
      size="small"
      {...(onClick === undefined ? {} : { onClick })}
      sx={{
        minHeight: 36,
        px: '14px',
        borderColor: 'border.default',
        fontSize: 12,
        fontWeight: 500,
        color: 'text.secondary',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': { borderColor: 'border.strong', bgcolor: 'action.hover' },
      }}
    >
      {children}
    </Button>
  );
}
